/**
 * Backend API 클라이언트 (사양서 §25).
 *
 * 모의 모드는 **기동 시 /health 확인이 실패했을 때만** 켠다. 그 뒤의 개별 요청 실패(500 ·
 * 네트워크 끊김)는 모의 데이터로 바꾸지 않고 호출부에 오류를 그대로 던진다 — 화면은 오류 상태와
 * 「다시 시도」를 그린다(리뷰 6회차 M20: 검색 500 한 번에 탭 전체가 모의 샘플로 바뀌고 돌아오지
 * 않았다. 모의 검색은 색 게이트가 없어 「파랑색」에 노랑 · 베이지가 섞였다 — R1-1 위반).
 */
import type { FabricDetail, SearchFilters, SearchResponse, SearchResultItem, SimilarResult } from '../types';
import { ApiError, authFetch, toApiError } from './auth';

/**
 * 내장 샘플(mock.ts, 29KB)은 모의 모드에서만 지연 로딩한다 — 실서버 번들에 섞이지 않게
 * (리뷰 5회차 m25: dist JS 301.8KB > 300KB).
 */
const loadMock = () => import('./mock');
/** 관리자 원단 편집 모의(R4) — 고친 값 · 휴지통을 모의 상세 · 검색 · 목록에 덧입힌다(모의 모드에서만 받는다) */
const loadEditMock = () => import('./fabricEditMock');

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const FORCE_MOCK = import.meta.env.VITE_FORCE_MOCK === 'true';

export const PAGE_SIZE = Number(import.meta.env.VITE_PAGE_SIZE ?? 8);
export const SHOW_REASON =
  (import.meta.env.VITE_SHOW_REASON ?? 'always') as 'always' | 'hover' | 'never';

export type Mode = 'live' | 'mock' | 'checking';

let mode: Mode = FORCE_MOCK ? 'mock' : 'checking';
const listeners = new Set<(m: Mode) => void>();

export function getMode(): Mode {
  return mode;
}

export function onModeChange(fn: (m: Mode) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function setMode(m: Mode) {
  if (mode === m) return;
  mode = m;
  listeners.forEach((fn) => fn(m));
}

/**
 * 기동 시 1회 서버 확인. 실패하면 이후 요청은 모두 mock 으로 간다.
 *
 * 200 응답만으로 판정하지 않는다 — SPA 개발/프리뷰 서버는 존재하지 않는
 * 경로에도 index.html 을 200 으로 돌려주므로, 서버가 없는데 'live' 로
 * 오판하여 모의 데이터 배너가 뜨지 않는다. 응답이 실제 API 인지
 * (JSON + service 필드) 확인해야 한다.
 */
async function checkHealth(): Promise<Mode> {
  if (FORCE_MOCK) return 'mock';
  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 2500);
    const r = await fetch(`${BASE}/health`, {
      signal: ctl.signal,
      headers: { Accept: 'application/json' },
    });
    clearTimeout(timer);
    const isJson = r.headers.get('content-type')?.includes('application/json');
    if (!r.ok || !isJson) return 'mock';
    const body = await r.json().catch(() => null);
    return body?.service === 'api' ? 'live' : 'mock';
  } catch {
    return 'mock';
  }
}

/**
 * 모드 판정 완료를 기다리는 단일 Promise.
 *
 * 이것이 없으면 페이지 컴포넌트의 최초 요청이 probe 완료 전에 나가
 * 'checking' 상태에서 live 경로를 타고, 서버가 없을 때 매번 실패 요청이
 * 발생한다(콘솔 500 → 폴백). 모든 API 호출은 이 Promise 를 먼저 await 한다.
 */
let probePromise: Promise<Mode> | null = null;

export function probe(): Promise<Mode> {
  probePromise ??= checkHealth().then((m) => { setMode(m); return m; });
  return probePromise;
}

/**
 * 모의 배너의 「다시 연결」 — 서버가 뒤늦게 떴는지 다시 본다. 실서버면 true.
 * 모의 데이터와 실데이터가 한 화면에 섞이지 않도록, 바뀌면 호출부가 화면을 새로 연다.
 */
export async function reprobe(): Promise<boolean> {
  const m = await checkHealth();
  return m === 'live';
}

/** 모드가 확정될 때까지 대기 (내부용). */
async function ready(): Promise<Mode> {
  return probe();
}

/**
 * 공개 API 요청. 토큰이 있으면 Bearer 를 붙인다 — 상세(§25.5)는 비로그인에게
 * 401 을 주고, 뷰어에게는 공급 필드를 가린 채 돌려주므로 누가 묻는지가 중요하다.
 */
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await authFetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!r.ok) throw await toApiError(r);
  return r.json() as Promise<T>;
}

/** 요청 취소(AbortController)인지 — 화면은 이 오류를 무시한다 (리뷰 H4) */
export function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

/**
 * 오류 → 화면 문장 한 쌍. 「서버 응답 오류」와 「연결 안 됨」을 구분한다 (리뷰 6회차 M20).
 * 서버 원문 코드값은 내보내지 않는다 — 상태 번호만 괄호로.
 */
export function describeError(e: unknown): { title: string; why: string } {
  if (e instanceof ApiError) {
    if (e.status >= 500) {
      return {
        title: '서버가 요청을 처리하지 못했습니다',
        why: `서버 응답 오류(${e.status})입니다. 서버가 다시 시작되는 중일 수 있습니다 — 잠시 뒤 「다시 시도」를 누르세요.`,
      };
    }
    if (e.status === 429) return { title: '요청이 너무 많습니다', why: '잠시 뒤 다시 시도하세요.' };
    return { title: '요청을 처리하지 못했습니다', why: `${e.message} (오류 ${e.status})` };
  }
  return {
    title: '서버에 연결할 수 없습니다',
    why: '네트워크 연결을 확인하고 「다시 시도」를 누르세요. 같은 Wi-Fi · 사내망에 있어야 합니다.',
  };
}

// ─── 검색 (§25.3) ──────────────────────────────────────────────
export interface SearchArgs {
  query: string;
  page?: number;
  pageSize?: number;
  filters?: SearchFilters & { pattern?: string; color?: string };
  rerank?: boolean;
  /** 질의 · 필터가 바뀌어 앞 요청이 쓸모없어지면 취소한다 (리뷰 H4) */
  signal?: AbortSignal;
  /**
   * 화면이 건수만 보려고 스스로 돌리는 검색(휴대폰 시트 「이대로 적용하면 n건」 · 고른 축의 건수 · 0건 원인 확인).
   * 서버 `options.preview` — 검색 기록(search_log)에 남지 않아 대시보드 「검색」 · 「결과 0건 검색」 에 섞이지 않는다 (9회차 M24).
   */
  preview?: boolean;
}

export async function search(args: SearchArgs): Promise<SearchResponse> {
  await ready();
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? PAGE_SIZE;

  if (mode === 'mock') {
    const ov = await loadEditMock();
    return ov.overlayPage((await loadMock()).searchMock(args.query, page, pageSize, {
      manufacturer: args.filters?.manufacturer ?? undefined,
      pattern: args.filters?.pattern,
      color: args.filters?.color,
      inStock: args.filters?.in_stock ?? undefined,
      material: args.filters?.material_contains ?? undefined,
      materials: args.filters?.materials ?? undefined,
      priceMax: args.filters?.price_max ?? undefined,
      currency: args.filters?.currency ?? undefined,
      after: args.filters?.registered_after ?? undefined,
    }));
  }

  return req<SearchResponse>('/api/v1/fabric/search', {
    method: 'POST',
    signal: args.signal,
    body: JSON.stringify({
      query: args.query,
      filters: args.filters ?? {},
      options: { rerank: args.rerank ?? null, page, page_size: pageSize, ...(args.preview ? { preview: true } : {}) },
    }),
  });
}

// ─── 전체 목록 (§25.3 · 컨셉 §7.5) ─────────────────────────────
export type ListSort = 'recent' | 'name' | 'part';
export type ListSince = 'today' | '7d' | '30d';

/** 목록의 정렬 · 필터 — 상세의 PREV / NEXT(neighbors)가 같은 값을 받는다 */
export interface ListQuery {
  sort: ListSort;
  since?: ListSince;
  color?: string;
  pattern?: string;
  manufacturer?: string;
  in_stock?: boolean;
  /** 섬유(쉼표로 여럿 — 모두 든 원단). 서버 FIBERS: polyester · spandex · nylon · cotton · rayon · wool · linen · acrylic · metallic */
  material?: string;
}

export interface ListItem extends SearchResultItem {
  registered_at?: string | null;
}

export interface ListResponse {
  total: number;
  /** 업무 시간대(tz) 오늘 0시 이후 등록된 완료 원단 — 다른 필터와 무관 */
  today: number;
  /** 「오늘」 의 시작(업무 시간대 자정, 예 2026-10-18T00:00:00+09:00) — 날짜 구분선도 이 기준 (R3-13) */
  today_start?: string;
  tz?: string;
  page: number;
  page_size: number;
  pages: number;
  sort: ListSort;
  since: ListSince | null;
  results: ListItem[];
  facets?: { color?: Array<{ value: string; count: number }>;
             pattern?: Array<{ value: string; count: number }>;
             manufacturer?: Array<{ value: string; count: number }>;
             material?: Array<{ value: string; count: number }>;
             /** 지금 조건 안에서 단가가 입력된 원단 수(값이 아니라 건수 — 단가 칸을 보일지 정한다, 2026-10-18 서버).
              *  operator 이상에게만 온다 — viewer · 비로그인 응답에는 키가 없다(없으면 0 이 아니라 「모름」) */
             priced?: number;
             /** 지금 조건 안에서 색 근거가 하나도 없어 어느 색 패싯에도 없는 원단 수 (2026-10-18 서버) */
             color_unknown?: number };
}

function listParams(q: ListQuery): URLSearchParams {
  const p = new URLSearchParams();
  p.set('sort', q.sort);
  if (q.since) p.set('since', q.since);
  if (q.color) p.set('color', q.color);
  if (q.pattern) p.set('pattern', q.pattern);
  if (q.manufacturer) p.set('manufacturer', q.manufacturer);
  if (q.in_stock) p.set('in_stock', 'true');
  if (q.material) p.set('material', q.material);
  return p;
}

/** GET /fabric/list — 홈 「최근 등록」과 「전체 원단」이 함께 쓴다. */
export async function listFabrics(
  q: ListQuery & { page: number; size: number; facets?: boolean },
  signal?: AbortSignal,
): Promise<ListResponse> {
  await ready();
  if (mode === 'mock') {
    const m = await loadMock();
    return (await loadEditMock()).overlayPage(m.listMock(q) as ListResponse);
  }
  const p = listParams(q);
  p.set('page', String(q.page));
  p.set('size', String(q.size));
  if (q.facets) p.set('facets', 'true');
  return req<ListResponse>(`/api/v1/fabric/list?${p.toString()}`, { signal });
}

/**
 * 공개 목록 — **인증을 보내지 않는다**(Codex 8차 B2). 로그인 · 가입 화면 콜라주와 빈 상태 견본 줄 같은 장식 사진 전용이다.
 * 예전에는 listFabrics(로그인했으면 Bearer)로 받아, 로그인 상태에서 만든 캐시가 로그아웃 뒤 로그인 화면에 남을 수 있었다
 * (서버 공개 정책 PUBLIC_ACCESS_LEVEL=none 이면 비로그인에게 보이면 안 되는 목록). 이제 비로그인과 같은 요청만 — 정책이 none 이면 401 이고
 * 부른 쪽은 사진 없이 조용히 물러난다. 요청 · 응답 형식은 GET /fabric/list 그대로다.
 */
export async function listPublic(q: ListQuery & { page: number; size: number }, signal?: AbortSignal): Promise<ListResponse> {
  await ready();
  if (mode === 'mock') return (await loadMock()).listMock(q) as ListResponse;
  const p = listParams(q);
  p.set('page', String(q.page));
  p.set('size', String(q.size));
  const r = await fetch(`${BASE}/api/v1/fabric/list?${p.toString()}`, { signal, credentials: 'omit', headers: { Accept: 'application/json' } });
  if (!r.ok) throw await toApiError(r);
  return r.json() as Promise<ListResponse>;
}

/**
 * 카탈로그 전체에서 단가가 입력된 원단 수 (operator 이상 · 루브릭 R3-2 「단가가 없으면 입력 대신 그 사실을」).
 * 목록 `facets.priced` 를 한 번 묻는다 — 예전에는 질의 없는 검색을 한 번 더 돌려 세어 검색 기록에 1줄씩 남았다(9회차 M24 · Codex).
 * 세션당 10분 캐시. 모의 모드는 샘플 단가를 센다.
 */
const PRICED_KEY = 'aitex.pricedCount';
export async function catalogPriced(): Promise<number | null> {
  try {
    const c = JSON.parse(sessionStorage.getItem(PRICED_KEY) ?? 'null') as { n: number; at: number } | null;
    if (c && Date.now() - c.at < 600_000) return c.n;
  } catch { /* 새로 센다 */ }
  const r = await listFabrics({ sort: 'recent', page: 1, size: 1, facets: true });
  // 2026-10-18 서버: priced 는 operator 이상에게만 온다 — 키가 없으면(세션이 끊겨 비로그인으로 받은 응답 등) 0 이 아니라 「모름」.
  // 「단가가 입력된 원단이 아직 없습니다」 를 잘못 말하지 않게 null 로 돌려주고 저장하지 않는다
  const n = r.facets?.priced;
  if (typeof n !== 'number') return null;
  try { sessionStorage.setItem(PRICED_KEY, JSON.stringify({ n, at: Date.now() })); } catch { /* 저장만 못 한다 */ }
  return n;
}

export interface Neighbors {
  position: number | null;
  total: number | null;
  prev: string | null;
  next: string | null;
}

/** GET /fabric/{id}/neighbors — 상세 PREV / NEXT 와 「n / 전체」(목록 전체 기준). */
export async function neighbors(id: string, q: ListQuery, signal?: AbortSignal): Promise<Neighbors> {
  await ready();
  if (mode === 'mock') return (await loadMock()).neighborsMock(id, q);
  return req<Neighbors>(`/api/v1/fabric/${encodeURIComponent(id)}/neighbors?${listParams(q).toString()}`, { signal });
}

// ─── 상세 (§25.5) ──────────────────────────────────────────────
/** 404 면 null. 401/403 · 서버 오류 · 연결 실패는 그대로 던진다 — 호출부가 안내를 그린다. */
export async function getFabric(id: string): Promise<FabricDetail | null> {
  await ready();
  if (mode === 'mock') return (await loadEditMock()).overlayDetail(await (await loadMock()).detailMockFor(id), id);
  try {
    return await req<FabricDetail>(`/api/v1/fabric/${encodeURIComponent(id)}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

// ─── 유사 검색 (§25.4) ─────────────────────────────────────────
/** 서버 유사 검색 응답 → 화면용. notice 는 임베딩 모델이 없을 때의 안내(한국어 문장). */
function toSimilar(j: { results?: SearchResultItem[]; mode?: string; notice?: string }): SimilarResult {
  return { results: j.results ?? [], mode: j.mode ?? null, notice: j.notice ?? null };
}
const mockSimilar = (results: SearchResultItem[]): SimilarResult => ({ results, mode: null, notice: null });

export async function similar(
  refId: string,
  opts: { brighter?: boolean } = {},
): Promise<SimilarResult> {
  await ready();
  if (mode === 'mock') return mockSimilar((await loadEditMock()).overlayItems((await loadMock()).similarMock(refId, opts.brighter)));

  const fd = new FormData();
  fd.append('reference_fabric_id', refId);
  if (opts.brighter !== undefined) {
    // §24.3 상대 비교 — 서버는 modifiers 로 받는다
    fd.append('modifiers', JSON.stringify([{
      attribute: 'lightness',
      direction: opts.brighter ? 'increase' : 'decrease',
      degree: 'slight',
    }]));
  }
  const r = await authFetch('/api/v1/fabric/similar-search', { method: 'POST', body: fd });
  if (!r.ok) throw await toApiError(r);
  return toSimilar(await r.json());
}

// ─── 이미지 검색 (§25.4 · 원 사양 검색2) ───────────────────────
/** 올린 사진에서 서버가 측정한 색 상위 3 — 응답 `query.colors` (계획서 U2, 2026-10-17) */
export interface QueryColor {
  hex: string;
  ratio: number;
  term: string;
  name_ko: string;
}

/** 사진 검색 결과 = 유사 검색 결과 + 측정 색. 서버가 주지 않으면 빈 배열(지어내지 않는다) */
export interface ImageSearchResult extends SimilarResult {
  colors: QueryColor[];
}

export async function searchByImage(
  full: File | Blob | null,
  macro: File | Blob | null,
  weightMacro: number,
): Promise<ImageSearchResult> {
  await ready();
  if (mode === 'mock') {
    // 모의 모드에서는 업로드 파일을 분석할 수 없다. 임의 원단 기준 유사검색으로 대체한다.
    return { ...mockSimilar((await loadMock()).similarMock('FAB000123')), colors: [] };
  }
  const fd = new FormData();
  if (full) fd.append('image_full', full, full instanceof File ? full.name : 'full.jpg');
  if (macro) fd.append('image_macro', macro, macro instanceof File ? macro.name : 'macro.jpg');
  fd.append('weight_full', String(1 - weightMacro));
  fd.append('weight_macro', String(weightMacro));
  const r = await authFetch('/api/v1/fabric/similar-search', { method: 'POST', body: fd });
  if (!r.ok) throw await toApiError(r);
  const j = await r.json();
  const colors: QueryColor[] = Array.isArray(j?.query?.colors)
    ? j.query.colors.filter((c: QueryColor) => c && typeof c.hex === 'string' && typeof c.ratio === 'number')
    : [];
  return { ...toSimilar(j), colors };
}
